import { doc, getDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useUserContext } from "../../../context/UserProvider";
import { db } from "../../../firebase";
import InviteActions from "./InviteActions";
import WelcomeDecor from "./WelcomeDecor";
import { buildInviteUrl, formatRaceDates, formatRaceDistance } from "./inviteLink";
import { saveRaceReady, trackRaceReady } from "./raceReadyStore";
import { countAllReferrals, getReferralProgress, referralGoalText } from "./referralRewards";
import "./RaceReady.css";

// Page 1 after registering (races with a welcome theme only, shown once):
// celebrate the new member, then ask them to bring a friend.
export default function WelcomeInvite({ theme, firstName, selectedRace, raceName, previewMode = false, onContinue }) {
  const { user, userData } = useUserContext();
  const referralCode = userData?.referral_link || "";
  const inviteUrl = buildInviteUrl(window.location.origin, raceName, referralCode, theme.promoCode);
  const bib = previewMode ? "1842" : `${selectedRace?.bibNumber ?? ""}`.trim();
  const distance = formatRaceDistance(selectedRace?.raceInfo);
  // Lifetime referral count (same number as the Referral Hub). null while loading.
  // In preview, add &previewReferrals=2 to the address to see the page as a member with 2 referrals.
  const [referralCount, setReferralCount] = useState(() => {
    if (!previewMode) return null;
    return Number(new URLSearchParams(window.location.search).get("previewReferrals")) || 0;
  });
  const facts = [distance && `Your goal: ${distance}`, formatRaceDates(selectedRace?.raceInfo), bib && `BIB #${bib}`].filter(Boolean);

  useEffect(() => {
    if (previewMode) return;
    saveRaceReady(user?.uid, selectedRace?.legId, { welcomeSeenAt: "now" });
    if (user?.uid) {
      getDoc(doc(db, "referrals", user.uid))
        .then((snapshot) => setReferralCount(countAllReferrals(snapshot.data())))
        .catch(() => setReferralCount(0));
    } else {
      setReferralCount(0);
    }
    trackRaceReady("race_welcome_viewed", { race: raceName, has_referral_link: Boolean(referralCode) });
    // Recorded once, when the page first appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = referralCount === null ? null : getReferralProgress(referralCount);

  const onShared = (method) => {
    if (!previewMode) trackRaceReady("race_invite_shared", { race: raceName, place: "welcome", method, referral_link: referralCode });
  };

  return (
    <div className="rr-welcome">
      <WelcomeDecor type={theme.decoration} />
      <img className="rr-welcome-logo" src={`${process.env.PUBLIC_URL || ""}${theme.logoUrl}`} alt={theme.raceLabel} />
      <p className="rr-eyebrow">Registration complete ✓</p>
      <h1 className="rr-welcome-title">
        {firstName ? `${firstName}, you're officially a ${theme.identity}` : `You're officially a ${theme.identity}`}
      </h1>
      {facts.length > 0 && (
        <div className="rr-chips">
          {facts.map((fact) => (
            <span key={fact} className="rr-chip">
              {fact}
            </span>
          ))}
        </div>
      )}

      <section className="rr-card rr-invite-card">
        <h2 className="rr-card-title">{theme.inviteTitle}</h2>
        <p className="rr-card-text">{theme.inviteText}</p>
        {theme.rewardsImage && (
          <img className="rr-rewards-image" src={`${process.env.PUBLIC_URL || ""}${theme.rewardsImage}`} alt="Referral rewards: stickers for 1 friend, socks for 5, a toque for 10, a free entry for 20" />
        )}
        {progress && progress.count > 0 && (
          <div className="rr-referral-count">
            <span className="rr-referral-number">{progress.count}</span>
            <div className="rr-referral-meta">
              <span className="rr-referral-label">{progress.count === 1 ? "friend has" : "friends have"} joined with your link</span>
              {progress.next && (
                <span className="rr-referral-bar" aria-hidden="true">
                  <span style={{ width: `${progress.percent}%` }} />
                </span>
              )}
            </div>
          </div>
        )}
        {progress && (progress.count > 0 || theme.inviteGoal) && (
          <p className="rr-invite-goal">{progress.count > 0 ? "" : "* "}{referralGoalText(progress.count, theme.inviteGoal)}</p>
        )}
        <InviteActions
          text={theme.shareText(Boolean(referralCode || theme.promoCode))}
          url={inviteUrl}
          subject={theme.shareSubject}
          onShared={onShared}
          primaryLabel="Share my invite link"
        />
      </section>

      <button type="button" className="rr-continue" onClick={onContinue}>
        Continue and get challenge-ready →
      </button>
    </div>
  );
}
